<script lang="ts">
	import { run } from 'svelte/legacy';

	import PortableText from '$components/PortableText/PortableText.svelte';
	import PdfEmbed from '$components/PdfEmbed.svelte';
	import { formatDatePair } from '$utils/formatDatePair';
	import convertToSlug from '$utils/slugify';

	import SvgIcon from '@samsveltecomponents/core/Icons/SvgIcon.svelte';
	import { soundcloud, youtube } from '@samsveltecomponents/core/Icons/icons';

	import type { InitialDataItem } from './common';
	import { resolve } from '$app/paths';
	type PageData = Pick<
		InitialDataItem,
		| 'name'
		| 'content'
		| 'duration'
		| 'startDate'
		| 'workLinks'
		| 'workCategory'
		| 'perusalScores'
		| 'relatedEvents'
		| 'completionDate'
		| 'summaryDetails'
		| 'instrumentation'
	> & {
		coverImage?: {
			srcset: string;
			src: string;
			sizes: string;
			metadata: {
				width: number;
				height: number;
			};
		};
	};

	interface Props {
		data: PageData;
	}

	let { data }: Props = $props();

	let coverImage:
		| { src: string; srcset: string; sizes: string; width: number; height: number }
		| undefined = $state();
	// eslint-disable-next-line @typescript-eslint/no-deprecated
	run(() => {
		if (data.coverImage) {
			coverImage = {
				src: data.coverImage.src,
				srcset: data.coverImage.srcset,
				sizes: data.coverImage.sizes,
				width: data.coverImage.metadata.width,
				height: data.coverImage.metadata.height
			};
		}
	});
</script>

<svelte:head>
	<title>{data.name}</title>
	<meta
		name="description"
		content={data.name +
			(data.summaryDetails ? ' - ' + data.summaryDetails : '') +
			(data.instrumentation ? ' - for ' + data.instrumentation : '')}
	/>
</svelte:head>

<main class="grid-cols-main-layout col-span-full grid grid-rows-[max-content_1fr]">
	<section
		id="title-section"
		class="grid-cols-main-layout relative col-span-full grid [:where(&>*)]:col-start-2"
		class:text-main-background={coverImage ? true : false}
		class:has-cover-image={coverImage ? true : false}
	>
		{#if coverImage}
			<img
				src={coverImage.src}
				srcset={coverImage.srcset}
				sizes={coverImage.sizes}
				alt="title background"
				class="absolute -z-10 col-span-full row-end-[9999] -mt-4 h-full w-full max-w-screen-2xl justify-self-center object-cover px-[calc(1.2rem-12px)] brightness-50"
				width={coverImage.width}
				height={coverImage.height}
			/>
		{/if}
		<ol>
			<li class="mh1 inline-block">
				<a data-sveltekit-preload-data class="text-link" href={resolve('/works')}> Works </a>
			</li>
			/
			<li class="mh1 inline-block">
				<a
					data-sveltekit-preload-data
					class="text-link"
					href={// eslint-disable-line svelte/no-navigation-without-resolve
					`/works#${convertToSlug(data.workCategory)}`}
				>
					{data.workCategory}
				</a>
			</li>
		</ol>
		<h1 class={'page-title' + (coverImage ? ' bg-black/75 backdrop-blur' : '')}>{data.name}</h1>
		<div class="w-50 mb-3 ml-auto mr-auto flex flex-wrap justify-around">
			{#if data.startDate != undefined || data.completionDate}
				<div class="text-center">({formatDatePair(data.startDate, data.completionDate)})</div>
			{/if}
		</div>
		<div class="ml-auto mr-auto flex w-full flex-wrap justify-around">
			{#if data.instrumentation}
				<div class="i min-w-50 mb-3 text-center">for {data.instrumentation}</div>
			{/if}
			{#if data.summaryDetails}
				<div class="i min-w-50 mb-3 text-center">{data.summaryDetails}</div>
			{/if}
			{#if data.duration}
				<div class="i min-w-50 mb-3 text-center">{data.duration} minutes</div>
			{/if}
		</div>
		<ul
			class="mb-4 mt-3 flex flex-wrap justify-center gap-1 pb-3 pt-2 text-center [.has-cover-image_&]:mb-8"
		>
			<!-- links -->
			{#if data.workLinks}
				<!-- eslint-disable-next-line svelte/require-each-key -->
				{#each data.workLinks as extraLink}
					{@const linkTitle =
						(extraLink.title ?? extraLink._type === 'soundcloud') ? 'Listen' : 'Watch'}
					{@const icon = extraLink._type === 'soundcloud' ? soundcloud : youtube}
					<li class="mh1 inline-block max-w-xs flex-grow">
						<a
							class="f5 bg-main-text/5 hover:text-blue-dark inline-block w-full px-2 py-1 hover:bg-blue-500/20 [.has-cover-image_&]:bg-black/10 [.has-cover-image_&]:backdrop-blur [.has-cover-image_&]:hover:bg-blue-200/60"
							href={// eslint-disable-line svelte/no-navigation-without-resolve
							extraLink.url}
							target="_blank"
							rel="noreferrer"
						>
							<SvgIcon {icon} class="mx-1 inline-block" />
							{linkTitle}</a
						>
					</li>
				{/each}
			{/if}
		</ul>
	</section>

	<section class="col-start-2 border-t-[.125rem] border-current py-10">
		<article class="prose-text-link-main prose mx-auto">
			{#if data.content}
				<PortableText value={data.content} />
			{/if}
		</article>
	</section>

	{#if data.perusalScores && data.perusalScores.length > 0}
		<section
			class="col-start-2 border-current pb-10 2xl:col-start-1 2xl:row-start-2 2xl:mr-10 2xl:w-[22.5rem] 2xl:justify-self-end 2xl:border-t-[.125rem] 2xl:px-1 2xl:pt-10"
		>
			<div class="mx-auto max-w-prose 2xl:sticky 2xl:top-16">
				<h1 class="pb-2 text-2xl font-bold">
					Perusal Score{#if data.perusalScores.length > 1}s{/if}
				</h1>
				<!-- eslint-disable-next-line svelte/require-each-key -->
				{#each data.perusalScores as perusalScoreSrc}
					<div class="h-[55rem] max-w-[34rem] 2xl:h-[40rem]">
						<PdfEmbed src="/perusal-scores/{perusalScoreSrc}" />
					</div>
				{/each}
			</div>
		</section>
	{/if}

	{#if data.relatedEvents.length > 0}
		<section
			class="col-start-2 border-t-[.125rem] border-current pt-10 xl:col-start-3 xl:row-start-2 xl:ml-10 xl:w-fit xl:px-1"
		>
			<div class="xl:sticky xl:top-16">
				<h2 class="font-bold">Related Events</h2>
				<ul>
					<!-- eslint-disable-next-line svelte/require-each-key -->
					{#each data.relatedEvents as event}
						<li>
							<!-- eslint-disable-next-line svelte/no-navigation-without-resolve -->
							<a class="text-link" href="/events/{event.slug}" data-sveltekit-preload-data
								>{event.name}</a
							>
						</li>
					{/each}
				</ul>
			</div>
		</section>
	{/if}
</main>
